import { ScribeButton as Button, ScribeText } from '../../components/scribe/ScribeUI';
import { scribeTokens as ui } from '../../components/scribe/tokens';
import React, { useEffect, useState } from 'react';
import { Box, IconButton, Menu, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import EditOutlined from '@mui/icons-material/EditOutlined';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import ShareOutlined from '@mui/icons-material/ShareOutlined';
import FormatColorTextOutlined from '@mui/icons-material/FormatColorTextOutlined';
import ManageSearchOutlined from '@mui/icons-material/ManageSearchOutlined';
import MoreHoriz from '@mui/icons-material/MoreHoriz';

// Collapsible report tools (Cath, panel space): the formatting toolbar and Check sources
// stay hidden until their header icon is pressed; "Save as a template" moves into ⋯.
// `tools` goes to the header, `editorTools` to TemplateView.
export function useReportTools() {
  const [format, setFormat] = useState(false);
  const [sources, setSources] = useState(false);
  const [saveSignal, setSaveSignal] = useState(0);
  return {
    tools: { format, sources, onToggleFormat: () => setFormat(value => !value), onToggleSources: () => setSources(value => !value), onSaveTemplate: () => setSaveSignal(value => value + 1) },
    editorTools: { format, sources, saveSignal },
  };
}

const iconButtonSx = on => ({ color: ui.colors.brand, borderRadius: `${ui.radius.control}px`, bgcolor: on ? ui.colors.brandSoft : 'transparent' });

function ToolIcon({ title, label = title, pressed, onClick, disabled, children, ...props }) {
  return <Tooltip title={title}><span>
    <IconButton size="small" aria-label={label} aria-pressed={pressed} onClick={onClick} disabled={disabled} sx={iconButtonSx(pressed)} {...props}>{children}</IconButton>
  </span></Tooltip>;
}

// `share` = { shared, linked, loading, onToggle }: the same MindVault sharing flag as the
// share icon in Files, so both places always agree.
function ShareButton({ share, compact = false }) {
  const [busy, setBusy] = useState(false);
  const disabled = busy || share.loading || !share.linked;
  const toggle = async () => {
    setBusy(true);
    try { await share.onToggle(); } finally { setBusy(false); }
  };
  const title = share.loading ? 'Checking MindVault connection…'
    : !share.linked ? 'Share to MindVault (connect this client to MindVault first)'
      : share.shared ? "Shared to the client's MindVault. Click to stop sharing." : "Share to the client's MindVault";
  const on = share.linked && share.shared;
  if (compact) return <ToolIcon title={title} label={on ? 'Shared' : 'Share'} pressed={share.linked ? Boolean(share.shared) : undefined} onClick={toggle} disabled={disabled}
    sx={{ ...iconButtonSx(false), ...(on && { color: '#0b7a52', bgcolor: '#e8f8f1' }) }}>
    {on ? <Check fontSize="small" /> : <ShareOutlined fontSize="small" />}
  </ToolIcon>;
  return <Tooltip title={title}>
    {/* span keeps the tooltip working while the button is disabled */}
    <span>
      <Button size="small" variant="outlined" disabled={disabled} onClick={toggle} aria-pressed={share.linked ? Boolean(share.shared) : undefined}
        startIcon={share.linked && share.shared ? <Check sx={{ fontSize: 16 }} /> : <ShareOutlined sx={{ fontSize: 16 }} />}
        sx={{ textTransform: 'none', ...(share.linked && share.shared && { color: '#0b7a52', borderColor: '#10a36e', backgroundColor: '#e8f8f1', '&:hover': { borderColor: '#0b7a52', backgroundColor: '#d1f2e3' } }) }}>
        {share.linked && share.shared ? 'Shared' : 'Share'}
      </Button>
    </span>
  </Tooltip>;
}

export default function ConsultationReportHeader({ item, onRename, onEdit, onClose, onDownload, share, tools, status, hideTitle = false, closeDisabled = false, editDisabled = false }) {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(item?.title || 'Report');
  const [saving, setSaving] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState(null);
  useEffect(() => { if (!renaming) setDraft(item?.title || 'Report'); }, [item?.title, renaming]);
  const save = async () => {
    if (!draft.trim() || !item?.id || !onRename) return;
    setSaving(true);
    try { if (await onRename(item.id, draft.trim())) setRenaming(false); }
    finally { setSaving(false); }
  };
  return <Box sx={{ mb: 1.5 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: .75, flexWrap: 'wrap' }}>
    {renaming ? <>
      <TextField autoFocus size="small" value={draft} onChange={event => setDraft(event.target.value)}
        onKeyDown={event => { if (event.key === 'Enter') save(); if (event.key === 'Escape') setRenaming(false); }}
        inputProps={{ 'aria-label': 'Report name' }} sx={{ flex: 1, minWidth: 140 }} />
      <IconButton size="small" disabled={saving || !draft.trim()} aria-label="Save report name" onClick={save} sx={{ color: ui.colors.brand }}><Check fontSize="small" /></IconButton>
      <IconButton size="small" disabled={saving} aria-label="Cancel report name edit" onClick={() => setRenaming(false)}><Close fontSize="small" /></IconButton>
    </> : <>
      {!hideTitle && <ScribeText kind="section" component="h3" title={item?.title || 'Report'} sx={{ flex: 1, minWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item?.title || 'Report'}</ScribeText>}
      {onRename && item?.id && <Tooltip title="Rename report"><IconButton size="small" aria-label="Rename report" onClick={() => setRenaming(true)}><EditOutlined sx={{ fontSize: 17 }} /></IconButton></Tooltip>}
    </>}
    {status && <Typography variant="caption" sx={{ color: ui.colors.muted }}>{status}</Typography>}
    {tools?.onToggleFormat && <ToolIcon title="Formatting toolbar" pressed={tools.format} onClick={tools.onToggleFormat}><FormatColorTextOutlined fontSize="small" /></ToolIcon>}
    {tools?.onToggleSources && tools.canCheckSources && <ToolIcon title="Check sources" pressed={tools.sources} onClick={tools.onToggleSources}><ManageSearchOutlined fontSize="small" /></ToolIcon>}
    {share && <ShareButton share={share} compact={Boolean(tools)} />}
    {tools && onDownload && <ToolIcon title="Export to PDF, Word or Excel" label="Export report" onClick={onDownload}><FileDownloadOutlined fontSize="small" /></ToolIcon>}
    {tools?.onSaveTemplate && <>
      <ToolIcon title="More actions" aria-haspopup="menu" aria-expanded={Boolean(moreAnchor)} onClick={event => setMoreAnchor(event.currentTarget)}><MoreHoriz fontSize="small" /></ToolIcon>
      <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
        <MenuItem onClick={() => { setMoreAnchor(null); tools.onSaveTemplate(); }}>Save as a template</MenuItem>
      </Menu>
    </>}
    {!tools && onDownload && <Tooltip title="Export to PDF, Word or Excel"><span><Button size="small" variant="outlined" aria-label="Export report" onClick={onDownload} startIcon={<FileDownloadOutlined sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none' }}>Export</Button></span></Tooltip>}
    {onEdit && <Button size="small" disabled={editDisabled} onClick={onEdit} sx={{ textTransform: 'none' }}>{editDisabled ? 'Locked' : 'Edit report'}</Button>}
    {onClose && <Button size="small" disabled={saving || closeDisabled} onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>}
  </Box>
    {share && !share.loading && !share.linked && <Typography variant="caption" component="p" sx={{ color: ui.colors.muted, mt: .5 }}>Share is available once this client is connected to MindVault.</Typography>}
  </Box>;
}
